import React from 'react';
import { ArrowUp, ArrowDown, Info } from 'lucide-react';
import Button from '../../../components/ui/Button';

const PlanModification = ({ subscription, onUpgrade }) => {
  const isProActive = subscription?.status === 'active' && subscription?.tier === 'pro';
  const willCancel = subscription?.cancelAtPeriodEnd;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Plan Modification</h2>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Upgrade Option */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <ArrowUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Upgrade Plan</h3>
              <p className="text-sm text-gray-600">Get more features instantly</p>
            </div>
          </div>

          <ul className="space-y-2 mb-6 text-sm text-gray-700">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-green-600 rounded-full"></span>
              10x higher spending limits
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-green-600 rounded-full"></span>
              Reduced transaction fees
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-green-600 rounded-full"></span>
              Priority support access
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-green-600 rounded-full"></span>
              Advanced analytics
            </li>
          </ul>

          <Button
            onClick={onUpgrade}
            disabled={isProActive && !willCancel}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
          >
            {isProActive && !willCancel ? 'Current Plan' : 'View Upgrade Options'}
          </Button>

          {isProActive && !willCancel && (
            <p className="text-xs text-gray-500 mt-2 text-center">You\'re already on the Pro plan</p>
          )}
        </div>

        {/* Downgrade/Cancel Option */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <ArrowDown className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Downgrade or Cancel</h3>
              <p className="text-sm text-gray-600">Manage your subscription</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Downgrade Policy</p>
                <p>
                  Downgrades take effect at the end of your current billing period. You\'ll keep Pro features until then.
                </p>
              </div>
            </div>
          </div>

          {willCancel ? (
            <div className="space-y-3">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                Your subscription will cancel on {new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}
              </div>
              <Button
                onClick={onUpgrade}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium"
              >
                Reactivate Subscription
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => alert('Please use the Stripe billing portal to cancel your subscription.')}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-2 rounded-lg font-medium"
            >
              Manage via Billing Portal
            </Button>
          )}
        </div>
      </div>

      {/* Prorated Billing Info */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-1">About Plan Changes</p>
            <ul className="space-y-1 text-gray-600">
              <li>• Upgrades are instant and prorated - you only pay for the remaining time in your billing cycle</li>
              <li>• Downgrades take effect at the end of your current billing period</li>
              <li>• You can cancel anytime - no cancellation fees</li>
              <li>• Refunds are available within 14 days of upgrade</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanModification;